import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp, getDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { uploadImageAsync } from '../utils/imageUpload';

interface BusinessProfile {
  businessName: string;
  businessType?: string;
  businessAddress?: string;
  businessDescription?: string;
  businessPhone?: string;
  businessEmail?: string;
  website?: string;
  logoUrl?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountHolder?: string;
  updatedAt?: any;
  version?: number;
  userId?: string;
}

const BusinessProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<BusinessProfile>({
    businessName: '',
    businessType: '',
    businessAddress: '',
    businessDescription: '',
    businessPhone: '',
    businessEmail: '',
    website: '',
    logoUrl: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountHolder: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [userCards, setUserCards] = useState<any[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const userId = getAuth().currentUser?.uid;
  
  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const result = t(key, options);
      return typeof result === 'string' ? result : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };
  
  // Helper function to get country-specific business type field info
  const getBusinessTypeFieldInfo = () => {
    const currentLanguage = i18n.language;
    
    switch (currentLanguage) {
      case 'ko':
        return {
          placeholder: safeT('businessProfile.businessTypeKoPlaceholder'),
          helper: safeT('businessProfile.businessTypeKoHelper')
        };
      case 'en':
        return {
          placeholder: 'ABN/Tax ID (e.g., 12 345 678 901)',
          helper: 'Australian Business Number (AU) | EIN (US) | Company Registration Number'
        };
      case 'ja':
        return {
          placeholder: '法人番号 (例: 1234567890123)',
          helper: '法人番号または個人事業主の届出番号を入力してください'
        };
      case 'zh':
        return {
          placeholder: '统一社会信用代码 (例: 91110000123456789X)',
          helper: '营业执照上的统一社会信用代码'
        };
      case 'vi':
        return {
          placeholder: 'Mã số thuế (VD: 0123456789)',
          helper: 'Mã số thuế doanh nghiệp hoặc cá nhân'
        };
      case 'de':
        return {
          placeholder: 'Umsatzsteuer-ID (z.B. DE123456789)',
          helper: 'Umsatzsteuer-Identifikationsnummer oder Handelsregisternummer'
        };
      case 'es':
        return {
          placeholder: 'NIF/CIF (ej: A12345674)',
          helper: 'Número de Identificación Fiscal o Código de Identificación Fiscal'
        };
      case 'fr':
        return {
          placeholder: 'SIRET (ex: 12345678901234)',
          helper: 'Numéro SIRET ou numéro d\'identification entreprise'
        };
      case 'id':
        return {
          placeholder: 'NPWP (cth: 12.345.678.9-012.345)',
          helper: 'Nomor Pokok Wajib Pajak atau NIB'
        };
      case 'pt':
        return {
          placeholder: 'CNPJ (ex: 12.345.678/0001-90)',
          helper: 'Cadastro Nacional da Pessoa Jurídica (Brasil)'
        };
      case 'th':
        return {
          placeholder: 'เลขประจำตัวผู้เสียภาษี (เช่น: 1234567890123)',
          helper: 'เลขประจำตัวผู้เสียภาษีนิติบุคคลหรือบุคคลธรรมดา'
        };
      case 'tr':
        return {
          placeholder: 'Vergi Numarası (örn: 1234567890)',
          helper: 'Vergi kimlik numarası veya ticaret sicil numarası'
        };
      default:
        return {
          placeholder: 'Business Registration Number',
          helper: 'Enter your business registration or tax identification number'
        };
    }
  };

  const businessTypeInfo = getBusinessTypeFieldInfo();
  
  // 앱과 동일한 getProfileRef 함수 추가
  const getProfileRef = (db: any) => {
    if (selectedCardId) {
      return doc(db, 'users', userId!, 'cards', selectedCardId, 'businessProfile', 'info');
    } else {
      return doc(db, 'users', userId!, 'businessProfiles', 'current');
    }
  };
  
  // 초기 프로필 로드
  useEffect(() => {
    if (!userId) {
      alert(safeT('businessProfile.errors.userError'));
      return;
    }

    const loadData = async () => {
      await fetchUserCards();
      // 카드가 없어도 일반 프로필 먼저 로드
      await fetchBusinessProfile();
    };
    
    loadData();
  }, [userId]);

  // 카드 선택이 변경될 때 비즈니스 프로필 다시 가져오기
  useEffect(() => {
    if (selectedCardId && userId) {
      console.log('BusinessProfilePage: Card selection changed, fetching profile for card:', selectedCardId);
      fetchBusinessProfile();
    }
  }, [selectedCardId, userId]);
  
  // 사용자의 카드 목록을 가져오는 함수
  const fetchUserCards = async () => {
    try {
      const db = getFirestore();
      const cardsRef = collection(db, 'users', userId!, 'cards');
      const cardsSnapshot = await getDocs(cardsRef);
      
      const cards = cardsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUserCards(cards);
      
      // 첫 번째 카드를 기본 선택하고 프로필 가져오기
      if (cards.length > 0 && !selectedCardId) {
        const firstCardId = cards[0].id;
        setSelectedCardId(firstCardId);
        console.log('BusinessProfilePage: Setting first card as selected:', firstCardId);
        
        // 카드 선택 후 프로필 가져오기
        setTimeout(() => {
          fetchBusinessProfile();
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching user cards:', error);
    }
  };
  









  const fetchBusinessProfile = async () => {
    if (!userId) {
      console.warn('BusinessProfilePage: No user ID available to fetch profile.');
      return;
    }

    console.log(`BusinessProfilePage: Attempting to fetch ${selectedCardId ? `business profile for card ID ${selectedCardId}` : 'general business profile'}...`);

    try {
      const db = getFirestore();
      
      // getProfileRef 함수를 사용하여 프로필 참조 가져오기
      const profileRef = getProfileRef(db);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const fetchedProfile = profileSnap.data();
        // 앱과 동일한 필드명으로 매핑 (기존 데이터 덮어쓰기)
        setProfile({
          businessName: fetchedProfile.businessName || '',
          businessType: fetchedProfile.businessType || fetchedProfile.abn || '', // 앱의 abn 필드도 확인
          businessAddress: fetchedProfile.businessAddress || fetchedProfile.address || '',
          businessDescription: fetchedProfile.businessDescription || '',
          businessPhone: fetchedProfile.businessPhone || fetchedProfile.phone || '',
          businessEmail: fetchedProfile.businessEmail || fetchedProfile.email || '',
          website: fetchedProfile.website || '',
          logoUrl: fetchedProfile.logoUrl || '',
          bankName: fetchedProfile.bankName || '',
          bankAccountNumber: fetchedProfile.bankAccountNumber || '',
          bankAccountHolder: fetchedProfile.bankAccountHolder || '',
        });
        console.log('BusinessProfilePage: Successfully fetched profile data:', fetchedProfile);
        return;
      }
      
      console.log('BusinessProfilePage: No business profile found - initializing empty profile');
      // 프로필이 없으면 완전히 빈 상태로 초기화
      setProfile({
        businessName: '',
        businessType: '',
        businessAddress: '',
        businessDescription: '',
        businessPhone: '',
        businessEmail: '',
        website: '',
        logoUrl: '',
        bankName: '',
        bankAccountNumber: '',
        bankAccountHolder: '',
      });
    } catch (error) {
      console.error("BusinessProfilePage: Error occurred while fetching business profile:", error);
    }
  };





  
  
  const updateBusinessProfile = async (updatedProfile: BusinessProfile) => {
    if (!userId) {
      throw new Error(safeT('businessProfile.errors.noUser'));
    }

    try {
      const db = getFirestore();
      
      // 앱과 동일한 로직: 선택된 카드에 저장
      if (selectedCardId) {
        console.log('BusinessProfilePage: Saving profile to selected card:', selectedCardId);
        const cardProfileRef = doc(db, 'users', userId, 'cards', selectedCardId, 'businessProfile', 'info');
        
        const profileToSave = {
          ...updatedProfile,
          userId: userId,
          updatedAt: serverTimestamp(),
          language: i18n.language
        };

        await setDoc(cardProfileRef, profileToSave, { merge: true });
        console.log('BusinessProfilePage: Successfully saved profile to card');
      } else {
        // 카드가 없으면 일반 비즈니스 프로필에 저장
        console.log('BusinessProfilePage: Saving profile to general business profile');
        const generalProfileRef = doc(db, 'users', userId, 'businessProfiles', 'current');
        
        const profileToSave = {
          ...updatedProfile,
          userId: userId,
          updatedAt: serverTimestamp(),
          language: i18n.language
        };

        await setDoc(generalProfileRef, profileToSave, { merge: true });
        console.log('BusinessProfilePage: Successfully saved profile to general');
      }
      
      setProfile(prev => ({ ...prev, ...updatedProfile }));
    } catch (error) {
      console.error("Error updating business profile:", error);
      throw error;
    }
  };
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userId) return;
    
    // 파일 크기 체크 (5MB 제한)
    if (file.size > 5 * 1024 * 1024) {
      alert(safeT('businessProfile.errors.fileTooLarge'));
      return;
    }
    
    // 파일 타입 체크
    if (!file.type.startsWith('image/')) {
      alert(safeT('businessProfile.errors.invalidFileType'));
      return;
    }
    
    setUploading(true);
    try {
      console.log('Starting logo upload for user:', userId);
      console.log('File info:', { name: file.name, size: file.size, type: file.type });
      
      // 앱과 동일한 uploadImageAsync 함수 사용
      const downloadURL = await uploadImageAsync(file, userId);
      console.log('Download URL obtained:', downloadURL);
      
      setProfile(prev => ({ ...prev, logoUrl: downloadURL }));
      console.log('Logo uploaded successfully:', downloadURL);
      
      // 성공 메시지
      alert(safeT('businessProfile.logoUploadSuccess'));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(safeT('businessProfile.errors.logoUploadFailed'));
    } finally {
      setUploading(false);
    }
  };
  
  const saveProfile = async () => {
    if (!userId) {
      alert(safeT('businessProfile.errors.userError'));
      return;
    }
    
    if (!(profile?.businessName || '').trim()) {
      alert(safeT('businessProfile.errors.nameRequired'));
      return;
    }
    
    setLoading(true);
    try {
      await updateBusinessProfile(profile);
      setSaved(true);
      
      alert(safeT('businessProfile.saveSuccess'));
      
      // 프로필 저장 후 인보이스 생성 페이지로 리다이렉트
      setTimeout(() => {
        navigate('/invoice/create');
      }, 1000);
    } catch (error) {
      console.error('Error saving business profile:', error);
      alert(safeT('businessProfile.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 프로필 완전 삭제 함수
  const deleteProfile = async () => {
    if (!userId) {
      alert(safeT('businessProfile.errors.userError'));
      return;
    }

    if (!confirm(safeT('businessProfile.confirmDelete'))) {
      return;
    }

    setLoading(true);
    try {
      const db = getFirestore();
      
      if (selectedCardId) {
        // 카드별 프로필 삭제
        const cardProfileRef = doc(db, 'users', userId, 'cards', selectedCardId, 'businessProfile', 'info');
        await setDoc(cardProfileRef, {});
        console.log('BusinessProfilePage: Deleted card profile');
      } else {
        // 일반 프로필 삭제
        const generalProfileRef = doc(db, 'users', userId, 'businessProfiles', 'current');
        await setDoc(generalProfileRef, {});
        console.log('BusinessProfilePage: Deleted general profile');
      }
      
      // 로컬 상태도 완전히 초기화
      setProfile({
        businessName: '',
        businessType: '',
        businessAddress: '',
        businessDescription: '',
        businessPhone: '',
        businessEmail: '',
        website: '',
        logoUrl: '',
        bankName: '',
        bankAccountNumber: '',
        bankAccountHolder: '',
      });
      
      alert(safeT('businessProfile.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting business profile:', error);
      alert(safeT('businessProfile.errors.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {safeT('businessProfile.title')}
            </h1>
            <p className="text-gray-600">
              {safeT('businessProfile.subtitle')}
            </p>
          </div>
          
          {/* Logo Upload */}
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div 
                className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                ) : profile?.logoUrl ? (
                  <img 
                    src={profile.logoUrl} 
                    alt="Business Logo"
                    className="w-full h-full rounded-full object-cover"
                    onError={() => setProfile(prev => ({...prev, logoUrl: ''}))}
                  />
                ) : (
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      {safeT('businessProfile.selectLogo')}
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
          
          {/* Form */}
          <div className="space-y-6">

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
                {safeT('businessProfile.basicInfo')}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessName')}
                  </label>
                  <input
                    type="text"
                    value={profile.businessName}
                    onChange={(e) => setProfile({...profile, businessName: e.target.value})}
                    placeholder={safeT('placeholders.businessName')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessType')}
                  </label>
                  <input
                    type="text"
                    value={profile.businessType || ''}
                    onChange={(e) => setProfile({...profile, businessType: e.target.value})}
                    placeholder={businessTypeInfo?.placeholder || safeT('placeholders.businessType')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {businessTypeInfo?.helper || ''}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessAddress')}
                  </label>
                  <textarea
                    value={profile.businessAddress || ''}
                    onChange={(e) => setProfile({...profile, businessAddress: e.target.value})}
                    placeholder={safeT('placeholders.businessAddress')}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessDescription')}
                  </label>
                  <textarea
                    value={profile.businessDescription || ''}
                    onChange={(e) => setProfile({...profile, businessDescription: e.target.value})}
                    placeholder={safeT('placeholders.businessDescription')}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessPhone')}
                  </label>
                  <input
                    type="tel"
                    value={profile.businessPhone || ''}
                    onChange={(e) => setProfile({...profile, businessPhone: e.target.value})}
                    placeholder={safeT('placeholders.businessPhone')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.businessEmail')}
                  </label>
                  <input
                    type="email"
                    value={profile.businessEmail || ''}
                    onChange={(e) => setProfile({...profile, businessEmail: e.target.value})}
                    placeholder={safeT('placeholders.businessEmail')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.website')}
                  </label>
                  <input
                    type="url"
                    value={profile.website || ''}
                    onChange={(e) => setProfile({...profile, website: e.target.value})}
                    placeholder={safeT('placeholders.website')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
                {safeT('businessProfile.bankInfo')}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.bankName')}
                  </label>
                  <input
                    type="text"
                    value={profile.bankName || ''}
                    onChange={(e) => setProfile({...profile, bankName: e.target.value})}
                    placeholder={safeT('placeholders.bankName')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.bankAccountNumber')}
                  </label>
                  <input
                    type="text"
                    value={profile.bankAccountNumber || ''}
                    onChange={(e) => setProfile({...profile, bankAccountNumber: e.target.value})}
                    placeholder={safeT('placeholders.bankAccountNumber')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {safeT('businessProfile.bankAccountHolder')}
                  </label>
                  <input
                    type="text"
                    value={profile.bankAccountHolder || ''}
                    onChange={(e) => setProfile({...profile, bankAccountHolder: e.target.value})}
                    placeholder={safeT('placeholders.bankAccountHolder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="mt-8">
            <button
              onClick={saveProfile}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {safeT('common.saving')}
                </div>
              ) : (
                safeT('businessProfile.saveAndComplete')
              )}
            </button>
          </div>
          
          {saved && (
            <div className="mt-4 flex items-center justify-center text-green-600">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {safeT('businessProfile.saved')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessProfilePage;
